import { __assign, __awaiter, __generator, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useEffect, useMemo, useRef, useState, } from 'react';
/* eslint-disable jsx-a11y/no-static-element-interactions */
import clsx from 'clsx';
import isString from 'lodash/isString';
import trimEnd from 'lodash/trimEnd';
import { clearReactReferencesInNode, clearWindowSelection, combineClasses, combineProps, styled, useDepsChange, useEventCallback, useRefState, useThemeProps, } from '../../foundation';
import { RcTooltip } from '../Tooltip';
import { InlineEditableStyle } from './styles';
import { RcInlineEditableClasses } from './utils';
var _RcInlineEditable = forwardRef(function (inProps, ref) {
    var _a, _b;
    var _c;
    var props = useThemeProps({ props: inProps, name: 'RcInlineEditable' });
    var onSave = props.onSave, automationId = props.automationId, tooltipTitle = props.tooltipTitle, _d = props.shouldRemoveNode, shouldRemoveNode = _d === void 0 ? true : _d, TooltipProps = props.TooltipProps, onChange = props.onChange, onKeyDown = props.onKeyDown, onFocus = props.onFocus, onBlur = props.onBlur, onMouseDown = props.onMouseDown, value = props.value, variant = props.variant, multiline = props.multiline, disabled = props.disabled, maxLength = props.maxLength, placeholder = props.placeholder, fullWidth = props.fullWidth, title = props.title, classesProp = props.classes, color = props.color, className = props.className, savingProp = props.saving, inputProps = props.inputProps, rest = __rest(props, ["onSave", "automationId", "tooltipTitle", "shouldRemoveNode", "TooltipProps", "onChange", "onKeyDown", "onFocus", "onBlur", "onMouseDown", "value", "variant", "multiline", "disabled", "maxLength", "placeholder", "fullWidth", "title", "classes", "color", "className", "saving", "inputProps"]);
    var _e = __read(useState(false), 2), isEditing = _e[0], setEditing = _e[1];
    var _f = __read(useState(false), 2), isSaving = _f[0], setSaving = _f[1];
    var _g = __read(useRefState(''), 2), draftRef = _g[0], setDraft = _g[1];
    var isNotNeedSaveWhenBlurRef = useRef(false);
    var textFieldRef = useRef();
    var labelRef = useRef(null);
    var saving = isSaving || savingProp;
    var handleSave = function (newValue, reason) { return __awaiter(void 0, void 0, void 0, function () {
        var outputValue;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    outputValue = multiline ? trimEnd(newValue) : newValue.trim();
                    if (!(value !== newValue)) return [3 /*break*/, 3];
                    if (!onChange) return [3 /*break*/, 1];
                    onChange === null || onChange === void 0 ? void 0 : onChange(outputValue, reason);
                    return [3 /*break*/, 3];
                case 1:
                    if (!onSave) return [3 /*break*/, 3];
                    // TODO: that method will be remove
                    setSaving(true);
                    return [4 /*yield*/, (onSave === null || onSave === void 0 ? void 0 : onSave(outputValue))];
                case 2:
                    _a.sent();
                    setSaving(false);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleKeyDown = useEventCallback(function (e) {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        var key = e.key, shiftKey = e.shiftKey;
        if (!isEditing || (multiline && key === 'Enter' && shiftKey)) {
            return;
        }
        var moveFocus = function () {
            var _a;
            e.stopPropagation();
            e.preventDefault();
            if (textFieldRef.current) {
                isNotNeedSaveWhenBlurRef.current = true;
                // * enter/esc should blur the input (tab does this automatically)
                textFieldRef.current.blur();
                // ! blur input will move the focus to body
                // * should trap the focus at current position for esc/tab to work
                (_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
            }
        };
        switch (key) {
            case 'Enter':
                moveFocus();
                handleSave(draftRef.current, 'confirm');
                break;
            case 'Escape':
                moveFocus();
                break;
            default:
                break;
        }
    });
    var handleFocus = useEventCallback(function (e) {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        setEditing(true);
    });
    var focusTextField = useEventCallback(function (e) {
        var _a;
        if (e.key === 'Enter') {
            (_a = textFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
            e.preventDefault();
        }
    });
    var handleBlur = useEventCallback(function (e) {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        // * clear selection for make that can select all when refocus
        clearWindowSelection();
        var isNeedSave = !isNotNeedSaveWhenBlurRef.current;
        // * when already save in keydown, not need save again
        if (isNeedSave) {
            handleSave(draftRef.current, 'leave');
        }
        setEditing(false);
        isNotNeedSaveWhenBlurRef.current = false;
    });
    var handleMouseDown = useEventCallback(function (e) {
        onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(e);
        if (disabled)
            return;
        setEditing(true);
    });
    var handleDraftChange = useEventCallback(function (e) {
        setDraft(e.target.value);
    });
    var handleFocusCapture = useEventCallback(function (e) {
        e.stopPropagation();
    });
    var classes = useMemo(function () { return combineClasses(RcInlineEditableClasses, classesProp); }, [classesProp]);
    useEffect(function () {
        return function () {
            if (shouldRemoveNode && textFieldRef && textFieldRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                clearReactReferencesInNode(textFieldRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var Component = multiline ? 'textarea' : 'input';
    useDepsChange(function () {
        if (!(isEditing || saving)) {
            setDraft(value, false);
        }
    }, [isEditing, saving, value]);
    var draft = draftRef.current;
    var displayValue = isEditing || saving ? draft : value;
    var toTooltipTitle = (_c = (isString(TooltipProps === null || TooltipProps === void 0 ? void 0 : TooltipProps.title) ? TooltipProps === null || TooltipProps === void 0 ? void 0 : TooltipProps.title : undefined)) !== null && _c !== void 0 ? _c : tooltipTitle;
    var children = (React.createElement("div", __assign({ ref: ref, "data-test-automation-id": automationId, onKeyDown: handleKeyDown, className: clsx(className, (_a = {},
            _a[classes.saving] = saving,
            _a)), onMouseDown: handleMouseDown }, rest),
        disabled ? null : (React.createElement(Component, __assign({ ref: textFieldRef, value: draft, autoComplete: "off", maxLength: maxLength, placeholder: placeholder }, __assign({}, (multiline
            ? {}
            : {
                type: 'text',
                'aria-label': toTooltipTitle,
            })), combineProps({
            onChange: handleDraftChange,
            onFocus: handleFocus,
            onBlurCapture: handleBlur,
            className: classes.textField,
        }, inputProps)))),
        React.createElement("div", { ref: labelRef, title: title || displayValue, className: clsx(classes.label, (_b = {},
                _b[classes.placeholder] = !displayValue,
                _b)), tabIndex: -1, onFocusCapture: handleFocusCapture, onKeyDown: disabled || saving ? undefined : focusTextField }, displayValue || placeholder)));
    if (!disabled && toTooltipTitle && (TooltipProps || tooltipTitle)) {
        return (React.createElement(RcTooltip, __assign({ tooltipForceHide: isEditing, disableFocusListener: true }, TooltipProps, { title: toTooltipTitle }), children));
    }
    return children;
});
var RcInlineEditable = styled(_RcInlineEditable)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), InlineEditableStyle);
RcInlineEditable.defaultProps = {
    variant: 'body1',
    color: 'neutral.f06',
};
RcInlineEditable.displayName = 'RcInlineEditable';
export { RcInlineEditable };
var templateObject_1;
